import { Document, HeadingLevel, Packer, Paragraph, SectionType, Table, TableRow, TableCell, TextRun } from "docx";
import { saveAs } from 'file-saver';

import { formatCurrency } from '../../../billing/timesheets/BillingTools';
// import logo from '../../../../app/coop-logo.png';
import dayjs from 'dayjs';
import { createBlobAttachment } from "../../../../services/firebase/StorageService";

const TitleSection = {
    properties: {
        type: SectionType.CONTINUOUS
    },
    children: [
        new Paragraph({
            heading: HeadingLevel.HEADING_1,
            children: [
                new TextRun("The Intermediary Cooperative")
            ]
        }),
        new Paragraph({
            heading: HeadingLevel.HEADING_2,
            children: [
                new TextRun("Booking Form")
            ]
        })

    ]
}

const captionCell = text => {
    return new TableCell({
        shading: {
            fill: 'DAD6D6'
        },
        children: [
            new Paragraph(text)
        ]
    });
}

const tabulate = (title, items) => {
    return [
        new Paragraph({
            heading: HeadingLevel.HEADING_3,
            children: [
                new TextRun(title)
            ]
        }),
        new Table({
            columnWidths: [3505, 5505],
            rows: items.map(item => {
                return new TableRow({
                    children: [
                        captionCell(item.caption),
                        new TableCell({
                            children: [
                                new Paragraph(item.text ?? '')
                            ]
                        }),
                    ]
                });
            })
        })
    ];
}

const makeContinuousSection = children => {
    return {
        properties: {
            type: SectionType.CONTINUOUS
        },
        children
    }
}


const BookingForm = ({ intermediary, referral, request, courtCase, quote, notes, court }) => {

    const needs = referral.vulnerabilities?.length > 0 ?
        referral.vulnerabilities.map(v => v.description)
        : [notes?.clientNeeds?.text || 'Unspecified'];

    const referenceSection = {
        properties: {
            type: SectionType.CONTINUOUS
        },
        children: [
            new Paragraph({
                children: [
                    new TextRun(`Prepared for ${referral.referrer?.name || '?'} at ${request.organization}`)
                ]
            }),
            new Paragraph({
                children: [
                    new TextRun(`Our Reference ${referral.bookingReference}`)
                ]
            })

        ]
    }

    // const caseSection = makeContinuousSection(tabulate(`Case Details`, [
    //     {caption: ``, text: ``},
    // ]))

    const caseSection = makeContinuousSection(tabulate(`Case Details`, [
        { caption: `Court`, text: court?.name },
        { caption: `Case Number`, text: courtCase?.courtCaseNumber }
    ]));

    const vulnerablePersonSection = makeContinuousSection(tabulate(`Vulnerable Person`, [
        { caption: `Name`, text: courtCase?.serviceUserName },
        { caption: `Vulnerabilities`, text: needs }
    ]));

    const intermediarySection = makeContinuousSection(tabulate(`Intermediary Details`, [
        { caption: `Name`, text: intermediary?.profile?.title },
        { caption: `Email Address`, text: intermediary?.profile?.email },
        { caption: `Contact Number`, text: intermediary?.profile?.phoneNumber },
    ]));

    const contactSection = makeContinuousSection(tabulate(`Please complete all details and return by ${dayjs().add(7, 'days').format('dddd DD MMMM YYYY')} to confirm your booking`, [
        { caption: `Email Address`, text: `` },
        { caption: `Contact Phone Number`, text: `` },
        { caption: `Invoice Contact Name`, text: `` },
        { caption: `Invoice Address`, text: `` },
        { caption: `Purchase Order Number`, text: `` },
    ]));

    const declarationSection = makeContinuousSection(tabulate(`I confirm that funding has been authorised by the court, and that the court has agreed to the terms and conditions`, [
        { caption: `Signature`, text: `` },
        { caption: `Date`, text: `` },
        { caption: `Name`, text: `` },
        { caption: `Designation / Role`, text: `` },
    ]));

    const servicesSection = {
        properties: {

        },
        children: [
            new Paragraph({
                heading: HeadingLevel.HEADING_3,
                children: [
                    new TextRun(`Schedule of Intermediary Services`)
                ]
            }),

            new Table({
                columnWidths: [3505, 1200, 1200, 1200, 1200],
                rows: [new TableRow({
                    children: [
                        captionCell(`Description`),
                        captionCell(`Date`),
                        captionCell(`Quantity`),
                        captionCell(`Rate`),
                        captionCell(`Net Amount`),
                    ]
                }),
                ...quote.items()
                    .filter(item => item.quantity > 0 && item.category === 'services')
                    .map(item => {
                        return new TableRow({
                            children: [
                                new TableCell({
                                    children: [
                                        new Paragraph(item.description)
                                    ]
                                }),
                                new TableCell({
                                    children: [
                                        new Paragraph(item.date || '-')
                                    ]
                                }),
                                new TableCell({
                                    children: [
                                        new Paragraph(`${item.quantity} ${item.measure}s`)
                                    ]
                                }),
                                new TableCell({
                                    children: [
                                        new Paragraph(formatCurrency(item.rate))
                                    ]
                                }),
                                new TableCell({
                                    children: [
                                        new Paragraph(formatCurrency(item.amount))
                                    ]
                                }),
                            ]
                        });
                    }),
                ]
            })

        ]
    }

    const totalRow = ({ caption, value }) => {
        return new TableRow({
            children: [
                new TableCell({
                    children: [
                        new Paragraph(caption)
                    ]
                }),
                new TableCell({
                    children: [
                        new Paragraph(formatCurrency(value))
                    ]
                }),
            ]
        })
    }

    const netTotal = quote.netTotal();
    const grandTotal = quote.grandTotal();
    const vat = grandTotal - netTotal;

    const summarySection = {
        properties: {
            type: SectionType.CONTINUOUS
        },
        children: [
            new Paragraph({
                heading: HeadingLevel.HEADING_3,
                children: [
                    new TextRun(`Summary of Estimated Fees & Expenses`),
                ]
            }),
            new Table({
                columnWidths: [3505, 1200],
                rows: [
                    totalRow({ caption: `Intermediary Services`, value: quote.netCategoryTotal('services') }),
                    totalRow({ caption: `Travel, Accommodation & Subsistence Expenses`, value: quote.netCategoryTotal('expenses') }),
                    totalRow({ caption: `Net Booking Total`, value: netTotal }),
                    totalRow({ caption: `VAT @ 20%`, value: vat }),
                    totalRow({ caption: `Gross Booking Total`, value: grandTotal }),
                ]
            })
        ]
    }

    return new Document({
        sections: [
            TitleSection,
            referenceSection,
            caseSection,
            vulnerablePersonSection,
            intermediarySection,
            contactSection,
            declarationSection,
            servicesSection,
            summarySection,
        ]
    });
};

const DownloadBookingForm = (digest) => {

    return new Promise(async (resolve, reject) => {
        try {
            const fileName = `booking-form-${dayjs().format('YYYYMMDD-HHmmss')}.docx`;
            const blob = await Packer.toBlob(BookingForm(digest));

            saveAs(blob, fileName);

            resolve(fileName);
        }
        catch (error) {
            reject(error);
        }
    })
        .catch(error => console.log(error));
}

const storeBookingForm = (digest) => {
    return new Promise(async (resolve, reject) => {
        try {
            const blob = await Packer.toBlob(BookingForm(digest));
            const url = await createBlobAttachment({ fullPath: `bookings/${digest.referral.organizationId}/${digest.referral.id}.docx`, blob });

            resolve(url);
        }
        catch (error) {
            reject(error);
        }
    })
        .catch(error => console.log(error));
}

export default DownloadBookingForm;

export { 
    storeBookingForm 
}